import { renderShadowHand } from './svg/ShadowHandSVG';
import { getAsset } from '../../services/assets';

const CONFIG = {
    ragCount: 20, // Number of shadowy trailing ribbons
    scale: 0.6,
    speed: 15.0, // Base speed of the lunge
    fadeDuration: 400
};

// Ribbons removed per user request

class CosmosParticle {
    x: number; y: number; vx: number; vy: number
    life: number; size: number; maxLife: number; isGold: boolean; rotation: number; rotSpeed: number

    constructor(startX: number, startY: number, intensive: boolean = false, isGold: boolean = false) {
        const angle = Math.random() * Math.PI * 2
        const dist = Math.random() * (intensive ? 150 : 50)
        this.x = startX + Math.cos(angle) * dist
        this.y = startY + Math.sin(angle) * dist
        const speed = intensive ? (5 + Math.random() * 15) : (1 + Math.random() * 4)
        this.vx = Math.cos(angle) * speed
        this.vy = Math.sin(angle) * speed
        this.maxLife = intensive ? 50 + Math.random() * 30 : 30 + Math.random() * 40
        this.life = this.maxLife
        this.size = intensive ? 4 + Math.random() * 8 : 2 + Math.random() * 5
        this.isGold = isGold
        this.rotation = Math.random() * Math.PI * 2
        this.rotSpeed = (Math.random() - 0.5) * 0.2
    }
    update() {
        this.x += this.vx; this.y += this.vy
        this.vx *= 0.92; this.vy *= 0.92 // Fast slow-down (stardust feel)
        this.life -= 1
        this.rotation += this.rotSpeed
    }
    draw(ctx: CanvasRenderingContext2D, alphaMultiplier: number = 1) {
        if (this.life <= 0) return
        const alpha = Math.max(0, this.life / this.maxLife) * alphaMultiplier
        ctx.globalCompositeOperation = 'lighter'

        ctx.save()
        ctx.translate(this.x, this.y)
        ctx.rotate(this.rotation)

        const coreColor = this.isGold ? `rgba(255, 215, 0, ${alpha})` : `rgba(150, 240, 255, ${alpha})`
        const glowColor = this.isGold ? `rgba(255, 140, 0, ${alpha * 0.8})` : `rgba(0, 150, 255, ${alpha * 0.8})`

        ctx.shadowBlur = 15; ctx.shadowColor = glowColor;
        ctx.fillStyle = coreColor

        // Draw 4-point star
        ctx.beginPath()
        ctx.moveTo(0, -this.size * 2)
        ctx.quadraticCurveTo(this.size * 0.2, -this.size * 0.2, this.size * 2, 0)
        ctx.quadraticCurveTo(this.size * 0.2, this.size * 0.2, 0, this.size * 2)
        ctx.quadraticCurveTo(-this.size * 0.2, this.size * 0.2, -this.size * 2, 0)
        ctx.quadraticCurveTo(-this.size * 0.2, -this.size * 0.2, 0, -this.size * 2)
        ctx.fill()

        // Inner intense core
        ctx.beginPath(); ctx.arc(0, 0, this.size * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff'; ctx.fill()

        ctx.restore()
        ctx.globalCompositeOperation = 'source-over'
    }
}

class NebulaParticle {
    x: number; y: number; size: number; life: number; maxLife: number; color1: string; color2: string; scale: number;

    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 100
        this.y = y + (Math.random() - 0.5) * 100
        this.size = 100 + Math.random() * 200
        this.maxLife = 60 + Math.random() * 60
        this.life = this.maxLife
        this.scale = 0.1

        const palettes = [
            ['rgba(0, 255, 255', 'rgba(0, 0, 139'], // Cyan to Dark Blue
            ['rgba(138, 43, 226', 'rgba(75, 0, 130'], // Magenta to Indigo
        ]
        const p = palettes[Math.floor(Math.random() * palettes.length)]
        this.color1 = p[0]
        this.color2 = p[1]
    }
    update() {
        this.life -= 1
        this.scale += 0.02
        this.y -= 0.5 // slowly drift up
    }
    draw(ctx: CanvasRenderingContext2D, alphaMultiplier: number = 1) {
        if (this.life <= 0) return
        const alpha = Math.max(0, Math.sin((this.life / this.maxLife) * Math.PI)) * alphaMultiplier * 0.6 // Smooth fade in/out

        ctx.globalCompositeOperation = 'screen'

        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * this.scale)
        grad.addColorStop(0, `${this.color1}, ${alpha})`)
        grad.addColorStop(0.5, `${this.color2}, ${alpha * 0.5})`)
        grad.addColorStop(1, `${this.color2}, 0)`)

        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.size * this.scale, 0, Math.PI * 2)
        ctx.fill()

        ctx.globalCompositeOperation = 'source-over'
    }
}

export type ShadowBurglaryEffect = {
    kind: 'shadowBurglary'
    t: number
    sx: number; sy: number
    tx: number; ty: number
    data?: {
        particles: CosmosParticle[]
        nebulae: NebulaParticle[]
        pos: { x: number, y: number }
        vel: { x: number, y: number }
        phase: 'MANIFEST' | 'LUNGE' | 'SNATCH' | 'RETREAT' | 'VANISH'
        cardAsset?: HTMLImageElement
        stolenCardSize: number
        initialDist: number
        angle: number
        timeInPhase: number
    }
}

export const createShadowBurglaryEffect = (sx: number, sy: number, tx: number, ty: number): ShadowBurglaryEffect => ({
    kind: 'shadowBurglary', t: 0, sx, sy, tx, ty
})

export const renderShadowBurglaryEffect = (
    eff: ShadowBurglaryEffect,
    ctx: CanvasRenderingContext2D
): boolean => {

    if (!eff.data) {
        const dx = eff.tx - eff.sx
        const dy = eff.ty - eff.sy
        const initialDist = Math.sqrt(dx * dx + dy * dy)
        const angle = Math.atan2(dy, dx)

        const cardImg = new Image();
        try {
            // we will use the global asset resolver for 'cards.back'
            const globalFn = (globalThis as any).getAsset;
            if (typeof globalFn === 'function') {
                const url = globalFn('cards.back');
                cardImg.src = url;
            } else {
                cardImg.src = getAsset('cards.back');
            }
        } catch (e) {
            console.error(e)
        }

        eff.data = {
            particles: [],
            nebulae: [],
            pos: { x: eff.sx, y: eff.sy },
            vel: { x: 0, y: 0 },
            phase: 'MANIFEST',
            cardAsset: cardImg,
            stolenCardSize: 0,
            initialDist,
            angle: angle + Math.PI / 2, // Natural orientation pointing up, to point vector we add 90 deg
            timeInPhase: 0
        }
    }

    const d = eff.data
    d.timeInPhase += 16.7 // Assumed ~60fps step
    const time = Date.now() * 0.001

    let grabProgress = 0;
    if (d.phase === 'SNATCH') grabProgress = Math.min(1.0, d.timeInPhase / 150);
    else if (d.phase === 'RETREAT' || d.phase === 'VANISH') grabProgress = 1.0;

    let globalAlpha = 1.0;

    const dx = eff.tx - d.pos.x;
    const dy = eff.ty - d.pos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Phase State Machine
    if (d.phase === 'MANIFEST') {
        globalAlpha = d.timeInPhase / 400;

        if (Math.random() > 0.4) d.particles.push(new CosmosParticle(eff.sx, eff.sy));
        if (Math.random() > 0.8) d.nebulae.push(new NebulaParticle(eff.sx, eff.sy));

        if (d.timeInPhase >= 400) {
            d.phase = 'LUNGE';
            d.timeInPhase = 0;
            const ndx = eff.tx - eff.sx;
            const ndy = eff.ty - eff.sy;
            const ndist = Math.sqrt(ndx * ndx + ndy * ndy);
            d.vel.x = (ndx / ndist) * CONFIG.speed;
            d.vel.y = (ndy / ndist) * CONFIG.speed;

            // Lunge burst
            for (let i = 0; i < 15; i++) d.particles.push(new CosmosParticle(eff.sx, eff.sy, true));
        }
    } else if (d.phase === 'LUNGE') {
        d.pos.x += d.vel.x;
        d.pos.y += d.vel.y;

        d.angle = Math.atan2(d.vel.y, d.vel.x) + Math.PI / 2; // +PI/2 points the fingers forward

        const stepDist = Math.sqrt(d.vel.x * d.vel.x + d.vel.y * d.vel.y);
        d.vel.x *= 1.05; d.vel.y *= 1.05;

        if (Math.random() > 0.2) d.particles.push(new CosmosParticle(d.pos.x, d.pos.y));
        if (Math.random() > 0.8) d.nebulae.push(new NebulaParticle(d.pos.x, d.pos.y));

        if (dist <= stepDist || dist < 30) {
            d.phase = 'SNATCH';
            d.timeInPhase = 0;
            d.pos.x = eff.tx;
            d.pos.y = eff.ty;

            // Store angles for smooth snatch rotation
            (d as any).snatchStartAngle = d.angle;

            // The retreat logic targets this tangent initially
            const cX = (eff.tx + eff.sx) / 2 + 150;
            const cY = (eff.ty + eff.sy) / 2 - 100;
            const tx_tangent = 2 * 1 * (cX - eff.tx);
            const ty_tangent = 2 * 1 * (cY - eff.ty);
            (d as any).snatchEndAngle = Math.atan2(ty_tangent, tx_tangent) - Math.PI / 2;

            d.vel.x = 0; d.vel.y = 0;

            // MASSIVE NEON/GOLD SHOCKWAVE
            for (let i = 0; i < 40; i++) d.particles.push(new CosmosParticle(eff.tx, eff.ty, true, true)); // Gold stars
            for (let i = 0; i < 30; i++) d.particles.push(new CosmosParticle(eff.tx, eff.ty, true, false)); // Cyan stars
            for (let i = 0; i < 5; i++) d.nebulae.push(new NebulaParticle(eff.tx, eff.ty));
        }
    } else if (d.phase === 'SNATCH') {
        d.stolenCardSize = Math.min(1.0, d.timeInPhase / 300);

        // Smooth rotation
        const snatchProgress = Math.min(1.0, d.timeInPhase / 400);
        // easeInOutCubic
        const ease = snatchProgress < 0.5 ? 4 * snatchProgress * snatchProgress * snatchProgress : 1 - Math.pow(-2 * snatchProgress + 2, 3) / 2;

        let startA = (d as any).snatchStartAngle || 0;
        let endA = (d as any).snatchEndAngle || 0;
        let diff = endA - startA;
        while (diff < -Math.PI) diff += Math.PI * 2;
        while (diff > Math.PI) diff -= Math.PI * 2;
        d.angle = startA + diff * ease;

        d.pos.x = eff.tx + (Math.random() - 0.5) * 8 * (1 - ease); // Shake lessens as it turns
        d.pos.y = eff.ty + (Math.random() - 0.5) * 8 * (1 - ease);

        if (Math.random() > 0.3) d.particles.push(new CosmosParticle(eff.tx, eff.ty, false, true)); // Gold sparks while holding

        if (d.timeInPhase >= 400) {
            d.phase = 'RETREAT';
            d.timeInPhase = 0;
        }
    } else if (d.phase === 'RETREAT') {
        const retProgress = Math.min(1.0, d.timeInPhase / 600);
        const cX = (eff.tx + eff.sx) / 2 + 150;
        const cY = (eff.ty + eff.sy) / 2 - 100;

        const nx = (1 - retProgress) * (1 - retProgress) * eff.tx + 2 * (1 - retProgress) * retProgress * cX + retProgress * retProgress * eff.sx;
        const ny = (1 - retProgress) * (1 - retProgress) * eff.ty + 2 * (1 - retProgress) * retProgress * cY + retProgress * retProgress * eff.sy;

        const tx_tangent = 2 * (1 - retProgress) * (cX - eff.tx) + 2 * retProgress * (eff.sx - cX);
        const ty_tangent = 2 * (1 - retProgress) * (cY - eff.ty) + 2 * retProgress * (eff.sy - cY);
        d.pos.x = nx;
        d.pos.y = ny;
        d.angle = Math.atan2(ty_tangent, tx_tangent) - Math.PI / 2; // -PI/2 points the wrist forward (pulling back)

        // Fade out during the last 30% of the retreat
        if (retProgress > 0.7) {
            globalAlpha = 1.0 - ((retProgress - 0.7) / 0.3);
        }

        if (Math.random() > 0.1) d.particles.push(new CosmosParticle(d.pos.x, d.pos.y, false, true));
        if (Math.random() > 0.8) d.nebulae.push(new NebulaParticle(d.pos.x, d.pos.y));

        if (retProgress >= 1) {
            for (let i = 0; i < 20; i++) d.particles.push(new CosmosParticle(eff.sx, eff.sy, true));
            return false; // End effect as soon as it reaches the owner
        }
    }

    // Update Particles
    for (let i = d.particles.length - 1; i >= 0; i--) {
        d.particles[i].update();
        if (d.particles[i].life <= 0) d.particles.splice(i, 1);
    }

    // Update Nebulae
    for (let i = d.nebulae.length - 1; i >= 0; i--) {
        d.nebulae[i].update();
        if (d.nebulae[i].life <= 0) d.nebulae.splice(i, 1);
    }

    // --- RENDER ---
    ctx.save();
    ctx.globalAlpha = Math.max(0, globalAlpha);

    // Draw Nebulae (Background)
    d.nebulae.forEach(n => n.draw(ctx, globalAlpha));

    // Draw Particles (Stars)
    d.particles.forEach(p => p.draw(ctx, globalAlpha));

    // Deep Space Mist Pool around hand origin
    const mistGrad = ctx.createRadialGradient(d.pos.x, d.pos.y, 0, d.pos.x, d.pos.y, 80 * CONFIG.scale);
    mistGrad.addColorStop(0, `rgba(138, 43, 226, ${0.4 * globalAlpha})`); // Magenta inner
    mistGrad.addColorStop(0.4, `rgba(0, 255, 255, ${0.2 * globalAlpha})`); // Cyan mid
    mistGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = mistGrad;
    ctx.fillRect(d.pos.x - 100, d.pos.y - 100, 200, 200);
    ctx.globalCompositeOperation = 'source-over';

    // Massive Golden Background Flash when grabbing the card
    if (d.phase === 'SNATCH' || d.phase === 'RETREAT') {
        const flashAlpha = d.phase === 'SNATCH' ? Math.min(1.0, d.timeInPhase / 150) : 1 - (d.timeInPhase / 600);
        ctx.globalCompositeOperation = 'screen';
        const goldGrad = ctx.createRadialGradient(d.pos.x, d.pos.y, 0, d.pos.x, d.pos.y, 250);
        goldGrad.addColorStop(0, `rgba(255, 215, 0, ${0.8 * flashAlpha})`); // Bright yellow/gold
        goldGrad.addColorStop(0.3, `rgba(255, 140, 0, ${0.4 * flashAlpha})`); // Orange
        goldGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = goldGrad;
        ctx.fillRect(d.pos.x - 250, d.pos.y - 250, 500, 500);
        ctx.globalCompositeOperation = 'source-over';
    }

    ctx.save();
    ctx.translate(d.pos.x, d.pos.y);
    ctx.rotate(d.angle);

    // If it's holding the stolen card
    if ((d.phase === 'SNATCH' || d.phase === 'RETREAT' || d.phase === 'VANISH') && d.cardAsset && d.stolenCardSize > 0) {
        ctx.save();
        ctx.translate(0, -20);
        ctx.rotate(Math.PI / 8);
        ctx.scale(d.stolenCardSize * 0.45, d.stolenCardSize * 0.45);

        // Intense core glow behind card
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 40;

        ctx.drawImage(d.cardAsset, -105, -165, 210, 330);

        // Apply cosmic neon filter on the card
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = 'rgba(0, 255, 255, 0.4)';
        ctx.fillRect(-105, -165, 210, 330);

        ctx.restore();
    }

    // Finally draw the hand over the card (SVG)
    // Wrap it in a negative translation to align SVG center with d.pos
    ctx.scale(CONFIG.scale, CONFIG.scale);
    ctx.translate(-150, -150);
    renderShadowHand(ctx, 300, 300, 1.0, grabProgress); // Provide grabProgress for finger curling

    ctx.restore();
    ctx.restore();

    return true;
}
